/**
 * @file 向随心搭注册编辑器相关方法
 * @document https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/2tsPs8CtSd/OwQGfaBCAW/At8b46mFJMUNhv
 */

import {getMorphoService, serviceNames} from '@/rte/editor';
// import {SetupBridge} from '@baidu/code-live-render';
import {getEditor} from './utils';
import {getCurrentCard} from './getCurrentCard';
import {Path, position} from './typings';
import {getInsertMethod, insertRow} from './insert';
import {getRemovePath} from './remove';


const injectMorphoAPI = (bridge: any, builderId: string) => {
    bridge.injectApi('Ku.editor.getByType', (type: string, range?: any) => {
        const editor = getEditor();
        if (!editor) {
            return [];
        }
        return editor.MorphoAPI.findNodesByType(editor, type, range);
    });

    bridge.injectApi('Ku.editor.getById', (id: string) => {
        const editor = getEditor();
        if (!editor) {
            return null;
        }
        return editor.MorphoAPI.findNodeById(editor, id);
    });

    bridge.injectApi('Ku.editor.getCurrentCard', () => {
        return getCurrentCard(builderId);
    });

    bridge.injectApi('Ku.editor.set', (attributes: any, at: Path | string, childIndex?: number) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        const transformService = getMorphoService(serviceNames.ITransformService);
        let path: Path = [];
        if (typeof at === 'string') {
            const atNode = editor.MorphoAPI.findNodeById(editor, at);
            if (atNode?.[1]) {
                path = atNode[1];
            }
        }
        else {
            path = at;
        }
        if (!path || path.length === 0) {
            return;
        }
        if (typeof childIndex === 'number') {
            path = path.concat(childIndex);
        }
        transformService.setNodes(attributes, {at: path});
    });

    // 插入结点
    bridge.injectApi('Ku.editor.insert', (
        // 插入类型
        type: string,
        at: position,
        options: {
            // 添加到结点上的属性
            attributes?: any;
            // 结点的内容，可以为字符串，也可以为结点、字符串数组
            content?: string | any[];
        }
    ) => {
        return getInsertMethod(builderId)(type, at, options);
    });

    bridge.injectApi('Ku.editor.insertMarkdown', (
        markdown: string,
        at: position
    ) => {
        return getInsertMethod(builderId)('', at, {markdown});
    });

    // 插入 html
    bridge.injectApi('Ku.editor.insertHTML', (
        html: string,
        at: position
    ) => {
        return getInsertMethod(builderId)('', at, {html});
    });

    // 表格中插入一行
    bridge.injectApi('Ku.editor.insertRow', insertRow);

    // 执行 MDSL
    bridge.injectApi('Ku.editor.runMDSL', (mdsl: string) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        editor.MorphoAPI?.runMDSL && editor.MorphoAPI.runMDSL(editor, mdsl);
    });

    bridge.injectApi('Ku.editor.serialize', (options: any) => {
        const editor = getEditor();
        if (!editor) {
            return '';
        }
        const customOptions = options.customOptions || {};
        const content = options.content || editor.children.slice(0);
        switch (options.format) {
            case 'plainText':
                return editor.mdSerializer.serialize(content, {editor, ...customOptions});
            case 'htmlForLLM':
                return editor.htmlSerializer.serialize(content, {
                    editor, forLLM: true, ...customOptions,
                });
            case 'markdown':
                return editor.mdSerializer.serialize(content, {
                    editor, standard: true, ...customOptions,
                });
            case 'html':
            default:
                return editor.htmlSerializer.serialize(content, {editor, ...customOptions});
        }
    });

    bridge.injectApi('Ku.editor.remove', (at: position) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        const path = getRemovePath(editor, at, builderId);
        if (path && path.length > 0) {
            const transformService = getMorphoService(serviceNames.ITransformService);
            transformService.removeNodes({
                at: path,
            });
        }
    });

    bridge.injectApi('Ku.editor.jumpTo', (options: {
        commentId?: string;
        blockId?: string;
        mentionId?: string;
        errorToastMessage?: string;
    }) => {
        const {commentId, blockId, mentionId} = options;
        if (!commentId && !blockId && !mentionId) {
            return;
        }
        const editor = getEditor();
        if (!editor) {
            return;
        }
        editor.MorphoAPI.scrollTo(editor, options);
    });
};

export {injectMorphoAPI};
